// Language name translator service using Intl.DisplayNames
// Uses CLDR data for accurate, localized language names

export interface LanguageOption {
  code: string
  name: string
  translatedName?: string
}

export class LanguageNameTranslator {
  private targetLanguage: string

  constructor(targetLanguage: string = 'en') {
    this.targetLanguage = targetLanguage
  }

  // Set the target language for translations
  setTargetLanguage(language: string): void {
    this.targetLanguage = language
  }

  // Get current target language
  getTargetLanguage(): string {
    return this.targetLanguage
  }

  // Custom language name mappings to override Intl.DisplayNames
  private getCustomLanguageName(languageCode: string): string | null {
    // Custom mappings for Chinese variants to ensure consistent regional naming
    const customMappings: Record<string, Record<string, string>> = {
      'zh': {
        'en': 'English',
        'zh': '中文（大陆）',
        'zh-tw': '中文（台灣）',
        'zh-hk': '中文（香港）',
        'hi': '印地语',
        'es': '西班牙语',
        'fr': '法语',
        'ar': '阿拉伯语',
        'bn': '孟加拉语',
        'pt': '葡萄牙语',
        'ru': '俄语'
      },
      'zh-tw': {
        'en': 'English',
        'zh': '中文（大陸）',
        'zh-tw': '中文（台灣）',
        'zh-hk': '中文（香港）',
        'hi': '印地語',
        'es': '西班牙語',
        'fr': '法語',
        'ar': '阿拉伯語',
        'bn': '孟加拉語',
        'pt': '葡萄牙語',
        'ru': '俄語'
      },
      'zh-hk': {
        'en': 'English',
        'zh': '中文（大陸）',
        'zh-tw': '中文（台灣）',
        'zh-hk': '中文（香港）',
        'hi': '印地語',
        'es': '西班牙語',
        'fr': '法語',
        'ar': '阿拉伯語',
        'bn': '孟加拉語',
        'pt': '葡萄牙語',
        'ru': '俄語'
      }
    }

    return customMappings[this.targetLanguage]?.[languageCode] || null
  }

  // Translate a language code using Intl.DisplayNames
  private translateLanguageCode(languageCode: string): string {
    // First, check for custom mappings
    const customName = this.getCustomLanguageName(languageCode)
    if (customName) {
      return customName
    }

    try {
      // Handle special cases for language codes
      const normalizedCode = this.normalizeLanguageCode(languageCode)

      const displayNames = new Intl.DisplayNames([this.targetLanguage], {
        type: 'language',
        fallback: 'code' // Use code if no translation available
      })

      const translated = displayNames.of(normalizedCode)
      return translated || languageCode // Fallback to original code if translation fails
    } catch (error) {
      console.warn(`Language translation failed for ${languageCode} to ${this.targetLanguage}:`, error)
      return languageCode // Return original code on error
    }
  }

  // Normalize language codes to match CLDR expectations
  private normalizeLanguageCode(code: string): string {
    // Handle special cases
    const codeMap: Record<string, string> = {
      'zh-TW': 'zh-Hant', // Traditional Chinese (Taiwan)
      'zh-HK': 'zh-Hant', // Traditional Chinese (Hong Kong)
      'zh': 'zh-Hans',    // Simplified Chinese (default)
    }

    return codeMap[code] || code
  }

  // Translate a list of language options
  translateLanguageList(languages: LanguageOption[]): LanguageOption[] {
    // If target language is English, no translation needed
    if (this.targetLanguage === 'en') {
      return languages.map(lang => ({
        ...lang,
        translatedName: lang.name
      }))
    }

    console.log(`🔍 Language Translator: Translating ${languages.length} language names to ${this.targetLanguage}`)

    try {
      // Apply translations to language list
      const translatedLanguages = languages.map(lang => ({
        ...lang,
        translatedName: this.translateLanguageCode(lang.code)
      }))

      console.log(`✅ Language Translator: Applied translations to ${translatedLanguages.length} languages`)
      return translatedLanguages

    } catch (error) {
      console.warn(`⚠️ Language Translator: Translation failed, using original names:`, error)

      // Fallback to original names
      return languages.map(lang => ({
        ...lang,
        translatedName: lang.name
      }))
    }
  }
}

// Create and export default instance
export const languageNameTranslator = new LanguageNameTranslator()

// Helper function for easier usage (synchronous now!)
export const translateLanguageNames = (
  languages: LanguageOption[],
  targetLanguage: string
): LanguageOption[] => {
  const translator = new LanguageNameTranslator(targetLanguage)
  return translator.translateLanguageList(languages)
} 